import { pull } from 'lodash';
import {
  DoubleSide,
  Mesh,
  Shape,
  ShapeGeometry,
  Vector2,
  Vector3,
} from 'three';
import { ShapeState3D } from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import { Point3D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { H3BasicMaterial } from '../../../materials/basic/h3-basic-material';
import { CuboidMaterial } from '../../../materials/cuboid/cuboid-material';
import { CUBE_SIZE } from '../../../state/cuboid-location-state-3d';
import { PointCloudContentState } from '../../../state/point-cloud-content-state';
import { determineRendererLayers } from '../../../utils/shape-visibility-utils';
import {
  getSideViewRendererLayers,
  setRendererLayers,
} from '../../render-utils';
import {
  ConfigureMeshesConfiguration,
  RenderConfiguration,
} from '../abstract-mesh-helper';
import { CuboidFace } from './cuboid-helper';
import { previewFaceOfInterest } from './face-of-interest-mesh.helper';

export class FaceOfInterestMesh extends Mesh<ShapeGeometry, H3BasicMaterial> {
  constructor() {
    super(
      new ShapeGeometry(
        new Shape([
          new Vector2(-CUBE_SIZE, 0),
          new Vector2(CUBE_SIZE, 0),
          new Vector2(CUBE_SIZE, CUBE_SIZE * 2),
          new Vector2(-CUBE_SIZE, CUBE_SIZE * 2),
        ]),
      ),
    );

    this.userData = { meshType: 'FaceOfInterestMesh' };
    this.frustumCulled = false;
  }

  dispose() {
    this.geometry.dispose();
  }

  placeFaceOfInterest(cuboidScale: Vector3, face = CuboidFace.Front) {
    const { position, rotation, getScale } = previewFaceOfInterest(face);
    this.position.set(...position);
    this.rotation.set(...rotation);
    this.translateZ(1e-3);
    this.setScale(getScale(cuboidScale.toArray()));
  }

  setScale(cuboidFaceScale: Point3D) {
    const scale = cuboidFaceScale.map((axisScale) => 1 / axisScale);
    const min = Math.min(...cuboidFaceScale.slice(0, 2)) / 4;
    this.scale.fromArray([scale[0], scale[1], 1]);

    const isRescaleMesh = min < CUBE_SIZE;
    if (isRescaleMesh) {
      this.scale.fromArray([scale[0] * min, scale[1] * min, 1]);
    }
  }

  updateRendererLayers(
    state: PointCloudContentState,
    renderConfig: RenderConfiguration,
    cuboidState: ShapeState3D,
  ) {
    const layers = determineRendererLayers(cuboidState, state, renderConfig);

    const faceOfInterestRendererLayers = pull(
      layers.slice(),
      ...getSideViewRendererLayers(),
    );
    setRendererLayers(faceOfInterestRendererLayers, this);
  }

  updateMaterial(
    { materialService }: ConfigureMeshesConfiguration,
    color: string,
  ) {
    this.material = materialService.get(CuboidMaterial, {
      color,
      transparent: true,
      side: DoubleSide,
    });
  }
}
